import { Chip, DropdownItem, InfoItem } from '@frk/eds-components';
import {
  AssetClass,
  FundId,
  FundShareClassId,
  ShareClassCode,
  SwitchState,
} from '.';

import { ProductType } from './product-type.type';
import { UsTax } from '@products/models/us-tax';

/**
 * ********** Tax Types **********
 */
export interface TaxState {
  taxData?: UsTax;
  years?: number[];
  currentYear?: number;
  noDataMessage?: string;
  federalTaxId?: string;
  stateCols?: number;
  pdf?: string;
  fundName?: string;
}

/**
 * ********** Capital Gains Types **********
 */
export interface CapGainsState {
  capGainListing: CapGainListing[]; // stores original formatted listing.
  gridListing: CapGainListing[]; // for, filtered listing.
  filters: CapGainFilterType[];
  hasNoShareClass?: boolean; // for filtered listing, which doesn't have share class for any row.
  hasLoadedFullFeed?: boolean;
}

export interface CapGainListing {
  fundId: FundId;
  fundName: string;
  shareClassCode: ShareClassCode;
  shareClassName: string;
  capGainFreq: string;
  productType: ProductType;
  taxYear: string;
  cusip: string;
  ticker: string;
  tableDetails: CapGainListingTableDetails[];
  fundLink: string;
  fundStatus: keyof typeof CapGainListingFundStatus;
  hasNoDistributions: boolean;
  investmentGroup: string;
  cards?: InfoItem[][];
  taId: string;
}

export interface CapGainListingTableDetails {
  capitalGainRecordDate: string;
  capitalGainExDividendDate: string;
  capitalGainPayableDate: string;
  capitalGainReInvestmentAmount: string;
  longTermCapitalGainAmount: string;
  shortTermCapitalGainAmount: string;
  totalDistributionAmountMonthly: string;
  estimateFlag: boolean;

  longTermCapitalGainPercent?: string;
  shortTermCapitalGainPercent?: string;

  totalGainNav?: string;
  navAsofDate?: string;
  navValue?: string;
}

export enum CapGainListingFundStatus {
  active = 'active',
  inactive = 'inactive',
  'soft-launch' = 'soft-launch',
  'soft-closed' = 'soft-closed',
}

export type CapGainFilterType = {
  filterLabel?: string;
  filterName: string;
  filterOptions: DropdownItem[];
};

export interface CapGainListingTotalPages {
  count: number;
}

export interface CapGainListingRecordsCount {
  total: number; // total number of records available from feed.
  filtered: number; // total number of records to be shown in paginated list (filtered list).
  size: number; // total number of records to be shown in one page.
}

export interface CapGainSelectedFilter {
  filterType: string;
  filterOptions: Chip[];
}

/**
 * ********** Capital Gains Estimates Types **********
 */

export interface CapGainsEstimatesState {
  showListing: boolean; // Flag to swap between search and listing page.
  filters?: CapGainsEstimatesFilterType[];
  recordDetails?: CapGainsEstimatesRecordDetails;
  action?: CapGainsEstimatesAction;
  isLoaded?: boolean;
}

export type CapGainsEstimatesFilterType = {
  filterLabel?: string;
  filterName: string;
  filterItems: DropdownItem[];
};

export interface CapGainsEstimatesRecordDetails {
  total: number;
  filtered: number;
  pageSize: number;
}

export enum CapGainsEstimatesAction {
  'INITIAL_LOAD', // Initial load of the page
  'FILTER_ROWS', // Filter selection(s) happened.
}

export interface CapGainsEstimatesListing {
  fundId: FundId;
  fundName: string;
  fundLink: string;
  fundStatus: keyof typeof CapGainListingFundStatus;
  shareClassCode: ShareClassCode;
  shareClassName: string;
  isPrimaryShareClass: boolean;
  productType: ProductType;
  assetClass?: AssetClass;
  cusip: string;
  ticker: string;
  taId: string;
  investmentGroup: string;
  capitalGainRecordDate: string;
  capitalGainExDividendDate?: string;
  capitalGainEstimateDate?: string;
  capitalGainPayableDate: string;
  longTermCapitalGainAmount: string;
  shortTermCapitalGainAmount: string;

  longTermCapitalGainPercent?: string;
  shortTermCapitalGainPercent?: string;

  totalGainNav?: string;
  navAsofDate?: string;
  navValue?: string;
  totalDistributionAmountMonthly: string;
  estimateFlag: boolean;
  actualsAlert: boolean;
  isSelected: boolean; // To check, if selected in ag-grid.
  isFavorite?: boolean;
}

export interface CapGainsEstimatesSelectedFilters {
  selectedDropdowns?: CapGainsEstimatesFilterType[];
  searchedText?: string;
}

export interface CapGainsEstimatesRowSelection {
  isSelected: boolean;
  selectedFundShareClassId?: FundShareClassId;
}

export interface CapGainsEstimatesFavoritesToggleState {
  showFavoritesOnlySwitch: boolean;
  favoritesOnlySwitchState?: SwitchState;
}

export interface CapGainsEstimatesColumnSortDetails {
  columnId: CapGainsEstimatesColumnId;
  sortType: CapGainsEstimatesColumnSortType;
}

// Below enum needed, as the column id has to be consistent for the sort functionality.
export enum CapGainsEstimatesColumnId {
  'FUND_SELECT' = 'FUND_SELECT',
  'FUND_NAME' = 'FUND_NAME',
  'SHARE_CLASS' = 'SHARE_CLASS',
  'LONG_TERM_CAP_GAINS_PERCENT' = 'LONG_TERM_CAP_GAINS_PERCENT',
  'SHORT_TERM_CAP_GAINS_PERCENT' = 'SHORT_TERM_CAP_GAINS_PERCENT',
  'TOTAL_PERCENT' = 'TOTAL_PERCENT',
  'LONG_TERM_CAP_GAINS' = 'LONG_TERM_CAP_GAINS',
  'SHORT_TERM_CAP_GAINS' = 'SHORT_TERM_CAP_GAINS',
  'NAV_AS_OF' = 'NAV_AS_OF',
  'TOTAL_CAP_GAINS' = 'TOTAL_CAP_GAINS',
  'RECORD_DATE' = 'RECORD_DATE',
  'PAYABLE_DATE' = 'PAYABLE_DATE',
  'EX_DATE' = 'EX_DATE',
  'TICKER' = 'TICKER',
}

export type CapGainsEstimatesColumnSortType = 'asc' | 'desc';
